/** Shared shapes for the Specialisation Carousel widget (section 3005, variant 3000). */

/** Layout picked from the CMS dropdown inside the single variant. */
export const SPECIALISATION_CAROUSEL_DESIGN_VARIANTS = {
  VARIANT_1: "variant_1",
  VARIANT_2: "variant_2",
} as const;

/** Per-card click behaviour. */
export const SPECIALISATION_CAROUSEL_CARD_ACTIONS = {
  LEAD_FORM: "lead_form",
  URL: "url",
  MODAL: "modal",
} as const;

export type SpecialisationCarouselCardAction =
  (typeof SPECIALISATION_CAROUSEL_CARD_ACTIONS)[keyof typeof SPECIALISATION_CAROUSEL_CARD_ACTIONS];

export interface SpecialisationCarouselCard {
  id?: string | number;
  meta_title?: string;
  /** variant_1 cover image. */
  meta_image?: string;
  meta_image_alt?: string;
  /** variant_2 card icon; falls back to the built-in box glyph. */
  meta_icon?: string;
  meta_icon_alt?: string;
  /** Per-card override of the built-in card arrow. */
  meta_arrow_icon?: string;
  meta_arrow_icon_alt?: string;

  meta_click_action?: SpecialisationCarouselCardAction | string;
  meta_url?: string;
  meta_open_in_new_tab?: boolean | number;

  /** Global course this card maps to (drives the lead form + brochure). */
  meta_course_id?: string | number;
  /** Auto-filled from the selected course; used when the course lookup misses. */
  meta_brochure_url?: string;

  meta_modal_title?: string;
  /** Rich-text HTML authored in the CMS. */
  meta_modal_body?: string;
  meta_modal_primary_cta_text?: string;
  meta_modal_brochure_cta_text?: string;
  meta_show_modal_brochure_cta?: boolean | number;

  meta_priority?: number;
  meta_status?: boolean | number;
}

export interface SpecialisationCarouselData {
  id?: string | number;
  status?: boolean;
  design_variant?: string;

  title?: string;
  title_color?: string;
  subtitle?: string;
  subtitle_color?: string;
  background_color?: string;

  card_background_color?: string;
  card_title_color?: string;
  card_arrow_color?: string;
  card_icon_color?: string;

  show_navigation?: boolean | number;
  nav_arrow_color?: string;
  nav_prev_icon?: string;
  nav_prev_icon_alt?: string;
  nav_next_icon?: string;
  nav_next_icon_alt?: string;

  show_pagination?: boolean | number;
  scroll_active_color?: string;
  scroll_inactive_color?: string;
  scrollbar_thumb_color?: string;
  scrollbar_track_color?: string;
  show_scrollbar?: boolean | number;

  show_cta?: boolean | number;
  cta_text?: string;
  cta_text_color?: string;
  cta_background_color?: string;
  cta_shadow_color?: string;
  cta_icon?: string;
  cta_icon_alt?: string;
  cta_url?: string;

  modal_header_background_color?: string;
  modal_body_background_color?: string;
  // No modal_title_color / modal_heading_color / modal_text_color: the modal's
  // words are the rich-text editor's, so their colours are authored there. The
  // columns still exist in the database but nothing reads them any more.
  modal_close_icon_color?: string;
  modal_close_icon?: string;
  modal_close_icon_alt?: string;
  modal_primary_cta_text?: string;
  modal_primary_cta_text_color?: string;
  modal_primary_cta_background_color?: string;
  modal_primary_cta_icon?: string;
  modal_primary_cta_icon_alt?: string;
  modal_brochure_cta_text?: string;
  modal_brochure_cta_text_color?: string;
  modal_brochure_cta_background_color?: string;
  modal_brochure_cta_border_color?: string;
  modal_brochure_cta_icon?: string;
  modal_brochure_cta_icon_alt?: string;

  metadata?: SpecialisationCarouselCard[];
}

/** Resolved (defaults applied) modal palette + copy handed to the modal. */
export interface SpecialisationCarouselModalTheme {
  headerBackgroundColor: string;
  bodyBackgroundColor: string;
  closeIconColor: string;
  closeIcon: string;
  closeIconAlt: string;
  primaryCtaText: string;
  primaryCtaTextColor: string;
  primaryCtaBackgroundColor: string;
  primaryCtaIcon: string;
  primaryCtaIconAlt: string;
  brochureCtaText: string;
  brochureCtaTextColor: string;
  brochureCtaBackgroundColor: string;
  brochureCtaBorderColor: string;
  brochureCtaIcon: string;
  brochureCtaIconAlt: string;
  ctaShadowColor: string;
}
